import {
  keepPreviousData,
  useQuery,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { CUSTOMER_COLUMN_FILTERS } from '@acs/shared'
import type { CustomerDetail, CustomerQuery, CustomerRow, Page } from '@acs/shared'
import { apiGet } from '@/lib/api'

/* ------------------------------------------------------------------ *
 * Patching the caches
 * ------------------------------------------------------------------ */

/**
 * Merge a patch per customer id into every cached page that holds one of
 * them, and into that customer's detail entry.
 *
 * ONE implementation for the three writers — the realtime stream, the table's
 * in-place edits and the drawer's — so an optimistic write and a server push
 * are indistinguishable to the rows, and so a field edited in the table
 * reaches an open drawer without waiting for the stream to say so. It used to
 * be two copies, one per file, which is exactly how the drawer would have come
 * to hear about some edits and not others.
 *
 * Every *page* of every filter is visited, so a control pressed on page 3 is
 * still correct when the operator pages back to it — but only pages that
 * actually hold a patched row are written: `setQueryData` always bumps
 * `dataUpdatedAt`, which re-renders every observer of that key, and skipping
 * the untouched pages is the difference between "one grid updates" and
 * "every cached grid re-renders". Rows keep their identity unless patched, so
 * memoised rows React never touches stay untouched.
 */
export function applyCustomerPatches(
  qc: QueryClient,
  patches: ReadonlyMap<string, Partial<CustomerRow>>
): void {
  if (patches.size === 0) return

  qc.setQueriesData<Page<CustomerRow>>(
    {
      queryKey: ['customers'],
      predicate: (query) => {
        const data = query.state.data as Page<CustomerRow> | undefined
        if (!data || !Array.isArray(data.rows)) return false
        return data.rows.some((row) => patches.has(row.id))
      },
    },
    (page) => {
      if (!page) return page
      let changed = false
      const rows = page.rows.map((row) => {
        const patch = patches.get(row.id)
        if (!patch) return row
        changed = true
        return { ...row, ...patch }
      })
      return changed ? { ...page, rows } : page
    }
  )

  // The detail panel is a superset of the row, so the same patch applies.
  // The updater returns `prev` (undefined) when the panel was never opened,
  // and setQueryData then does nothing — no phantom cache entry.
  for (const [id, patch] of patches) {
    qc.setQueryData<CustomerDetail>(['customer', id], (prev) =>
      prev ? { ...prev, ...patch } : prev
    )
  }
}

/**
 * Canonical cache key for a customer list view.
 *
 * The query object is normalised first — empty filters dropped, `q` trimmed,
 * multi-selects sorted — so that two filter states that mean the same thing
 * hash to the same key and share one cache entry (and one network request).
 * The `'customers'` prefix is load-bearing: `useRealtime()` patches every
 * cached page by prefix-matching it.
 *
 * `limit` and `offset` are part of the key, so each page of a filter is its
 * own entry. That is what makes paging back and forth instant: page 1 is still
 * in the cache when the operator returns to it, and a realtime patch reaches
 * it whether or not it is the page on screen.
 */
export function customersKey(q: CustomerQuery): readonly unknown[] {
  return ['customers', normalizeQuery(q)] as const
}

/** Mirrors `DEFAULT_PAGE_SIZE` in customer-toolbar.tsx. Kept as a literal so a
 *  data hook does not have to import from a component. */
const DEFAULT_LIMIT = 50

function normalizeQuery(q: CustomerQuery): Record<string, unknown> {
  const out: Record<string, unknown> = {}

  if (q.status) out['status'] = q.status
  if (q.mode && q.mode.length > 0) out['mode'] = [...q.mode].sort()
  if (q.agent && q.agent.length > 0) out['agent'] = [...q.agent].sort()
  /*
   * The exclusion half, and it has to be listed here or it does not exist.
   *
   * This is the third time that sentence has had to be written in this file
   * (see the column filters and `activeHit` below), and the failure is always
   * the same shape: the view asks for it, the request goes out without it, and
   * the list is silently WIDER than it should be. Here that means Telegram
   * customers reappearing in Pending with nothing to explain why.
   *
   * Sorted like the other multi-selects, so one cache entry serves any order.
   */
  if (q.agentNot && q.agentNot.length > 0) out['agentNot'] = [...q.agentNot].sort()
  if (q.liveState && q.liveState.length > 0) out['liveState'] = [...q.liveState].sort()
  // Listed here or it does not exist — this function is the whitelist the
  // column filters fell through. Sorted, like the other multi-selects, so
  // "day, night" and "night, day" share one cache entry and one request.
  if (q.shift && q.shift.length > 0) out['shift'] = [...q.shift].sort()
  // `1`, not `true`: the API reads either, and this keeps the URL and the cache
  // key short and identical to what the rail's link produces.
  if (q.won === true) out['won'] = 1
  /*
   * The other outcome flag, and it was missing — the same whitelist bug the
   * column filters had below, on the link next to the one that already hit it.
   *
   * `toCustomerQuery` set `activeHit`, the URL carried it, the tab lit up, and
   * this function dropped it on the floor: the Active hits list was every
   * approved customer, sorted by a field almost none of them have. Nothing
   * reported it, because an over-broad list looks like a list.
   */
  if (q.activeHit === true) out['activeHit'] = 1

  const term = q.q?.trim()
  if (term) out['q'] = term

  if (q.jobId) out['jobId'] = q.jobId
  if (q.scheduleId) out['scheduleId'] = q.scheduleId

  /*
   * The header filter row, as a loop over the shared list rather than a line
   * each.
   *
   * This function is the last gate before the network, and it is a whitelist:
   * a key it does not copy is not sent and is not in the cache key either. Six
   * of the seven column filters were written by hand everywhere else and simply
   * never listed here, so the URL said `?name=prince`, the table refetched, and
   * the request went out without it — a filter that visibly did nothing, with
   * no error anywhere to explain it.
   *
   * `CUSTOMER_COLUMN_FILTERS` is the wire contract's own list and `CustomerQuery`
   * extends it, so this cannot fall behind again.
   */
  for (const key of CUSTOMER_COLUMN_FILTERS) {
    const value = q[key]?.trim()
    if (value) out[key] = value
  }

  out['sort'] = q.sort ?? 'created_at'
  out['dir'] = q.dir ?? 'desc'
  out['limit'] = q.limit ?? DEFAULT_LIMIT
  out['offset'] = q.offset ?? 0
  if (q.withTotal !== undefined) out['withTotal'] = q.withTotal

  return out
}

export function useCustomers(q: CustomerQuery): UseQueryResult<Page<CustomerRow>> {
  // The request params and the cache key are the *same* normalised object,
  // so a cache hit can never disagree with what was asked for.
  const params = normalizeQuery(q)
  return useQuery({
    queryKey: customersKey(q),
    queryFn: ({ signal }) => apiGet<Page<CustomerRow>>('/api/customers', params, signal),
    // Paging and filtering must not blank the table: hold the previous page on
    // screen (dimmed by `isPlaceholderData` at the call site) while the next
    // one loads. Without this, every Next click unmounts the whole grid — a
    // flash of empty chrome, a lost scroll position, and a footer that briefly
    // reads "of —". With it, the rows swap in place.
    placeholderData: keepPreviousData,
  })
}

export function useCustomerDetail(id: string | null): UseQueryResult<CustomerDetail> {
  return useQuery({
    queryKey: ['customer', id] as const,
    queryFn: ({ signal }) => {
      if (!id) throw new Error('useCustomerDetail: queryFn ran without an id')
      return apiGet<CustomerDetail>(`/api/customers/${encodeURIComponent(id)}`, undefined, signal)
    },
    enabled: !!id,
  })
}
